import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import {
	usePassVanRequestToCreditCommitteeMutation,
	PassVanRequestToCreditCommitteeInput,
	PassVanRequestToCreditCommitteeMutation
} from '@/graphql/__generated__/van-credits';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import logger from '@/logger';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';

interface PassToCommitteeProps {
	requestId: string;
	currentCreditAreaOpinion?: string;
	refetchData: () => Promise<any>;
}

const PassToCreditCommittee: React.FC<PassToCommitteeProps> = ({
	requestId,
	currentCreditAreaOpinion,
	refetchData
}) => {
	const t = useTranslations('VanCreditRequestSimulation');
	const [creditAreaOpinion, setCreditAreaOpinion] = useState('');
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const creditRequests = useApolloClient(SCHEMAS.VAN_CREDITS);
	useEffect(() => {
		if (currentCreditAreaOpinion) {
			setCreditAreaOpinion(currentCreditAreaOpinion);
		}
	}, [currentCreditAreaOpinion]);

	const [passToCreditCommittee, { loading }] = usePassVanRequestToCreditCommitteeMutation({
		client: creditRequests,
		onCompleted: async (data: PassVanRequestToCreditCommitteeMutation) => {
			const passToCommitteeResult = data.passVanRequestToCreditCommittee;
			if (passToCommitteeResult?.successful) {
				logger.success('Passed to committee successfully', passToCommitteeResult);
				toast.success(t('PassedToCommitteeSuccessfully'));
				await refetchData();
			}
			if (
				passToCommitteeResult?.mutationErrors &&
				passToCommitteeResult.mutationErrors.length > 0
			) {
				let hasFieldErrors = false;

				passToCommitteeResult.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								if (error.field === 'credit_area_opinion') {
									setError(error.message || t('CreditAreaOpinionRequired'));
									hasFieldErrors = true;
								}
								break;
							case 'ActionFailed':
								logger.error('Action failed', error.message);
								break;
							case 'InvalidAction':
								logger.error('Invalid action', error.message);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});

				if (!hasFieldErrors) {
					toast.error(t('ErrorPassingToCommittee'));
				}
			}
		},
		onError: (error) => {
			logger.error('Error passing to committee', error);
			toast.error(t('ErrorPassingToCommittee'));
		}
	});

	const handlePassToCommittee = async () => {
		if (!creditAreaOpinion || creditAreaOpinion.trim() === '') {
			setError(t('CreditAreaOpinionRequired'));
			return;
		}

		const input: PassVanRequestToCreditCommitteeInput = {
			requestId,
			creditAreaOpinion
		};

		await passToCreditCommittee({ variables: { input } });
		setConfirmModalOpen(false);
	};

	const creditAreaOpionionHint =
		t('CreditAreaOpinionLength') + '. ' + creditAreaOpinion.length + '/3400';

	return (
		<div className="px-6 mt-4 print:hidden">
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className="grow">
					<Typography weight="semibold">{t('CreditAreaOpinionTitle')}</Typography>
				</div>
				<div className="w-full">
					<TextArea
						rows={4}
						value={creditAreaOpinion}
						onChange={(prev) => setCreditAreaOpinion(prev.target.value)}
						placeholder={t('CreditAreaOpinionPlaceholder')}
						disabled={loading || !!currentCreditAreaOpinion}
						error={!!error}
						hint={error || creditAreaOpionionHint}
						maxChars={3400}
						strictMaxChars
					/>
					<div className="mt-5 justify-end flex">
						<Tooltip content={currentCreditAreaOpinion ? t('CreditAreaOpinionDisabled') : ''}>
							<Button
								label={t('PassToCommittee')}
								onClick={() => setConfirmModalOpen(true)}
								loading={loading}
								size="auto"
								disabled={
									loading ||
									!creditAreaOpinion ||
									creditAreaOpinion.trim() === '' ||
									!!currentCreditAreaOpinion
								}
							/>
						</Tooltip>
					</div>
				</div>
			</div>
			<ConfirmModal
				isOpen={confirmModalOpen}
				onClose={() => setConfirmModalOpen(false)}
				onCancel={() => setConfirmModalOpen(false)}
				onConfirm={handlePassToCommittee}
				title={t('ConfirmPassToCommittee')}
				cancelBtnLoading={loading}
				confirmBtnLoading={loading}
				confirmActionText={t('PassToCommittee')}
			/>
		</div>
	);
};

export default PassToCreditCommittee;
